/**
 * Compact token count for display. Values above 999 are shown in kilo-tokens with
 * one decimal digit (e.g. 2400 → "2.4k", 24000 → "24.0k"); smaller values are shown
 * as-is (e.g. 850 → "850").
 */
export function formatTokenCount(n: number): string {
  if (n > 999) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

/**
 * Human-readable byte size (e.g. 512 → "512 B", 15360 → "15.0 KB", 2684354560 → "2.5 GB").
 * Binary units (1024), one decimal digit above the unit threshold.
 */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return unit === 0 ? `${Math.round(value)} B` : `${value.toFixed(1)} ${units[unit]}`;
}

/**
 * USD price formatted with exactly two decimals (e.g. 12.345 → "$12.35", 0.004 → "$0.00").
 * Use alongside {@link usdTitle} to surface the precision that gets rounded away.
 */
export function formatUsd(n: number): string {
  return '$' + n.toFixed(2);
}

/**
 * Hover-hint for a USD value that is displayed via {@link formatUsd}. Returns the value at
 * four decimals (e.g. "0.0042") when that reveals precision the 2-decimal display rounds away,
 * otherwise `undefined` (nothing was truncated, so no hint is needed).
 */
export function usdTitle(n: number): string | undefined {
  const full = n.toFixed(4);
  return Number(full) === Number(n.toFixed(2)) ? undefined : full;
}

/**
 * Per-million-token rate hint, e.g. 2 → "$2.00 / 1M tokens". Rates are natively ≤2 decimals,
 * so no separate precision hint is required.
 */
export function usdRate(n: number): string {
  return `$${n.toFixed(2)} / 1M tokens`;
}

/**
 * Timestamp for log entries. When the entry's date is today (local timezone) only the time is
 * shown (e.g. "14:05:09"); otherwise the full "Mon D, HH:MM:SS" form is used (e.g. "Sep 12, 09:31:44").
 */
export function formatLogTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
  return isToday
    ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
